import type { UseQueryResult } from "@tanstack/react-query";

export type TODO = unknown;

/* Type level helper for the satsifies operator */
export type Satisfies<T extends Base, Base> = T;

/** Makes all properties of a nested type visible */
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

export type DeepReadonly<T> = {
  readonly [P in keyof T]: DeepReadonly<T[P]>;
};

export type DeepRequired<T> = {
  [P in keyof T]-?: DeepRequired<T[P]>;
};

export type DeepOptional<T> = {
  [P in keyof T]?: DeepOptional<T[P]>;
};

/** Parses an object type and keeps it's structure, but maps every native type value to the set value.  */
export type DeepMapNativeObjectKeysToValue<T, Value> = {
  [K in keyof T]?: T[K] extends string | number | symbol | undefined
    ? Value
    : T[K] extends Record<string | number | symbol, any> | undefined // eslint-disable-line
      ? DeepMapNativeObjectKeysToValue<T[K], Value>
      : never;
};

export type Modify<T, R> = Omit<T, keyof R> & {
  [P in keyof R as R[P] extends never ? never : P]: R[P];
};

export type OnlyOne<T extends object> = {
  [K in keyof T]: { [P in keyof T]: K extends P ? T[K] : never };
}[keyof T];

export function isDefined<T>(value: T | null | undefined): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

/** Drop the first element of any tuple */
export type Shift<T extends unknown[]> = T extends [unknown, ...infer R] ? R : never;

/** Omit from an object union type. It is different from the default omit, in a way that the default omit omits the key from the unified object,
 * while this omits the key from each member of the union separately.
 *
 * @example
 * type A = { a: string; b: number };
 * type B = { a: string; c: boolean };
 * type MyUnion = A | B;
 * type Result = OmitFromObjectUnion<MyUnion, 'a'>; // Omit<A, 'a'> | Omit<B, 'a'> = { b: number } | { c: boolean }
 * */
export type OmitFromObjectUnion<T, K extends keyof T> = T extends object ? Omit<T, K> : never;

/** Omit a key from an object, but make it optional and never */
export type OmitWithNever<T, K extends keyof T> = Omit<T, K> & {
  [key in K]?: never;
};

/**
 *  Extracts the return type of an useQuery hook.
 *
 *  Example:
 *  ```ts
 *  const useComments = () => {
 *      return useQuery(...)
 *  }
 *
 *  type res = ExtractUseQueryResult<typeof useComments>
 *  ```
 *
 *  This also works:
 *  ```ts
 *  type res = ExtractUseQueryResult<ReturnType<typeof useComments>>
 *  ```
 */
export type ExtractUseQueryResult<T, E = unknown> = T extends () => UseQueryResult<infer U, E>
  ? U
  : T extends UseQueryResult<infer U, E>
    ? U
    : never;

/** Builds a type safe type predicate filter function that can be used to narrow the type of an object that is of a union object type.
 * Eg. ProjectViewLinkUser = ProjectViewLinkInvitation | ProjectViewLinkMember */
export const buildTypePredicateFilter =
  <BaseType extends object, PredicateType extends BaseType>(
    key: Exclude<keyof PredicateType, keyof BaseType>
  ): ((value: BaseType) => value is PredicateType) =>
  (value): value is PredicateType =>
    key in value;

export const buildAdvancedTypePredicateFilter =
  <BaseType extends object, PredicateType extends BaseType>(opts: {
    required: (keyof PredicateType)[];
    excluded?: string[];
  }): ((value: BaseType) => value is PredicateType) =>
  (value): value is PredicateType =>
    opts.required.every((k) => k in value) && (opts.excluded?.every((k) => !(k in value)) ?? true);

export const isNonNullable = <T>(value: T): value is NonNullable<T> =>
  value !== null && value !== undefined;
